/**
 * /tenants/$slug/access-governance — unified queue route shim.
 */

import { createFileRoute } from "@tanstack/react-router";
import {
  queueFilterSchema,
  type QueueEntry,
  type QueueFilter,
  type QueueResponse,
} from "@/features/access-governance/schemas";
import {
  attestationsQueryKey,
  decisionsQueryKey,
  fetchAttestations,
  fetchDecisions,
  fetchQueue,
  fetchThread,
  threadQueryKey,
} from "@/features/access-governance/queries";
import { AccessGovernancePage } from "@/features/access-governance/pages/access-governance-page";

export const Route = createFileRoute(
  "/_authed/tenants/$slug/access-governance/",
)({
  validateSearch: (raw: Record<string, unknown>): QueueFilter =>
    queueFilterSchema.parse(raw),
  // `selected` is in loaderDeps so the loader fires when the
  // operator picks a different request. The queue ensureQueryData
  // call is keyed only by `filter`, so re-running on `selected`
  // changes hits the cache for the queue (no refetch) — the new
  // work is just the activity-prefetch for the newly selected row.
  // Without this, the detail panel's tabs would render empty for
  // ~150-300ms on each cold cache navigation, then collapse.
  loaderDeps: ({ search }) => ({
    filter: {
      kind: search.kind,
      priority: search.priority,
      status: search.status,
      sla: search.sla,
      page: search.page,
    } as QueueFilter,
    selected: search.selected,
  }),
  loader: async ({ context, deps }) => {
    const tenantID = context.tenant.id;
    const queue = await context.queryClient.ensureQueryData<QueueResponse>({
      queryKey: ["access-governance", "queue", tenantID, deps.filter],
      queryFn: () => fetchQueue(tenantID, deps.filter),
    });

    // Resolve the selected entry. Falls back to first row if the
    // URL `selected` is stale or absent — mirrors the
    // self-heal effect in AccessGovernancePage so loader prefetch
    // and runtime selection target the same row.
    const target: QueueEntry | undefined =
      (deps.selected && queue.data.find((e) => e.ID === deps.selected)) ||
      queue.data[0];
    if (!target) return queue;

    // Activity prefetch — fire the three queries in parallel
    // WITHOUT awaiting. TanStack Query dedupes by key, so when
    // the component mounts and useThreadQuery / useDecisionsQuery
    // / useAttestationsQuery start their queries they latch onto
    // the in-flight promises started here rather than firing
    // duplicates. The cache is being populated by the time the
    // panel mounts; any flicker is bounded to the gap between
    // mount and the first fetch resolving (typically <100ms).
    //
    // Awaiting here was the previous attempt to eliminate the
    // flicker entirely, but it suspended route navigation on the
    // slowest of the three responses — clicking between requests
    // felt like 200-500ms of "did anything happen?" Fire-and-
    // forget is the better trade: navigation is instant, panel
    // populates as data arrives.
    //
    // Errors are intentionally swallowed by the runtime queries
    // (ActivitySections has anyError fallthrough), so we don't
    // need an error catch here. The void cast is just to make
    // the discarded-promise intent explicit to lint.
    const { Kind, ID } = target;
    void context.queryClient.ensureQueryData({
      queryKey: threadQueryKey(tenantID, Kind, ID),
      queryFn: () => fetchThread(tenantID, Kind, ID),
    });
    void context.queryClient.ensureQueryData({
      queryKey: decisionsQueryKey(tenantID, Kind, ID),
      queryFn: () => fetchDecisions(tenantID, Kind, ID),
    });
    // Attestations is recovery-only on the read side; prefetch
    // unconditionally because the endpoint returns an empty
    // array for access kind (cheap) and gating here would mean
    // duplicating the kind check.
    void context.queryClient.ensureQueryData({
      queryKey: attestationsQueryKey(tenantID, Kind, ID),
      queryFn: () => fetchAttestations(tenantID, Kind, ID),
    });
    return queue;
  },
  component: AccessGovernancePage,
});
